/**
 * Opens the Userback feedback widget.
 *
 * The widget only loads once the visitor has granted functional consent, so it
 * is not always on the page. When it is missing *because consent was refused*
 * we open the cookie preferences instead of doing nothing: the visitor asked to
 * send feedback, so telling them what it needs is better than a button that
 * appears broken. Every other reason for it being absent has to stay silent —
 * sending someone to a cookie dialog they have already answered reads as the
 * platform having lost their choice.
 *
 * Returns true when the widget was actually opened.
 */
import { CONSENT_CATEGORIES, isGranted } from './cookie-consent'

let neverLoads = false

/**
 * Record that this build will never load the widget (it is skipped in
 * development), so its absence is not mistaken for a consent problem.
 *
 * Set from main.ts rather than read from `import.meta.env` here, which the lint
 * parser does not accept in a plain `.js` module.
 */
export function markFeedbackWidgetUnavailable () {
  neverLoads = true
}

/**
 * The widget's own API, or null while it has not finished loading.
 *
 * The loader parks a stub on `window.Userback` the moment it starts, so the
 * object being there proves nothing — only a real `open`, and `isLoaded()` once
 * the widget script has landed, do.
 */
function loadedWidget () {
  const widget = window.Userback

  if (!widget || typeof widget.open !== 'function') {
    return null
  }

  if (typeof widget.isLoaded === 'function' && !widget.isLoaded()) {
    return null
  }

  return widget
}

export function openFeedbackWidget () {
  const widget = loadedWidget()

  if (widget) {
    widget.open()

    return true
  }

  // Older widget builds expose only the launcher. It is hidden site-wide with
  // `display: none`, which does not stop a programmatic click.
  const button = document.querySelector('.userback-button-e')

  if (button instanceof HTMLElement) {
    button.click()

    return true
  }

  // Deliberately not loaded in this build, so consent is not why it is missing
  // and the preferences dialog would be a red herring.
  if (neverLoads) {
    return false
  }

  // Absent for a reason the visitor can act on — offer the toggle.
  if (!isGranted(CONSENT_CATEGORIES.FUNCTIONAL) && typeof window.openCookiePreferences === 'function') {
    window.openCookiePreferences()
  }

  // Otherwise it simply failed to load (blocked by an extension, or still in
  // flight). Nothing the visitor can fix in a dialog.
  return false
}
